import { useState } from "react";
import { ExternalLink, Loader2, TrendingUp, Coins, ShieldAlert } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useMyFinancial } from "@/hooks/use-queries";
import { cn } from "@/lib/utils";

const EVENT_TYPE_LABELS: Record<string, string> = {
  ESCROW_RELEASED: "Escrow Released",
  ESCROW_DEPOSITED: "Escrow Deposited",
  ESCROW_REFUNDED: "Escrow Refunded",
  DISPUTE_RESOLVED: "Dispute Resolved",
};

const BID_TX_TYPE_LABELS: Record<string, string> = {
  FREE_MONTHLY_RESET: "Free Bid Reset",
  BID_DEBIT: "Bid Debit",
  BID_REFUND: "Bid Refund",
  USDC_PURCHASE: "USDC Purchase",
  MANUAL_ADJUSTMENT: "Manual Adjustment",
};

const EVENT_TYPE_FILTERS = ["ALL", "ESCROW_RELEASED", "ESCROW_DEPOSITED", "ESCROW_REFUNDED", "DISPUTE_RESOLVED", "BID_DEBIT", "BID_REFUND"];

type Row = {
  id: string;
  date: string;
  agent: string;
  type: string;
  typeLabel: string;
  amount: string;
  amountSign: "+" | "-" | "";
  status: string;
  txHash?: string;
};

export default function Financial() {
  const { data, isLoading } = useMyFinancial();
  const [filter, setFilter] = useState("ALL");

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  const jobEvents = data?.jobEvents ?? [];
  const bidTxs = data?.bidTxs ?? [];

  // Build unified rows
  const allRows: Row[] = [
    ...jobEvents.map((e: any) => ({
      id: e.id,
      date: e.createdAt,
      agent: e.job?.agentId ?? "—",
      type: e.type,
      typeLabel: EVENT_TYPE_LABELS[e.type] ?? e.type,
      amount: e.job?.budgetUsdc ? `${Number(e.job.budgetUsdc).toFixed(2)} USDC` : "—",
      amountSign: (e.type === "ESCROW_RELEASED" ? "+" : e.type === "ESCROW_REFUNDED" ? "+" : "") as "+" | "-" | "",
      status: "CONFIRMED",
      txHash: e.payload?.txHash as string | undefined,
    })),
    ...bidTxs.map((tx: any) => ({
      id: tx.id,
      date: tx.createdAt,
      agent: tx.allowance?.agent?.name ?? "—",
      type: tx.type,
      typeLabel: BID_TX_TYPE_LABELS[tx.type] ?? tx.type,
      amount: tx.type === "BID_DEBIT" || tx.type === "BID_REFUND" ? `${Math.abs(tx.amount)} bids` : tx.usdcTxHash ? `${Math.abs(tx.amount)} USDC` : `${Math.abs(tx.amount)} bids`,
      amountSign: (tx.amount > 0 ? "+" : tx.amount < 0 ? "-" : "") as "+" | "-" | "",
      status: "CONFIRMED",
      txHash: tx.usdcTxHash as string | undefined,
    })),
  ].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

  const filteredRows = filter === "ALL" ? allRows : allRows.filter((r) => r.type === filter);

  // Summary stats
  const totalEarned = jobEvents
    .filter((e: any) => e.type === "ESCROW_RELEASED")
    .reduce((sum: number, e: any) => sum + Number(e.job?.budgetUsdc ?? 0), 0);

  const bidCreditsUsed = bidTxs
    .filter((tx: any) => tx.type === "BID_DEBIT")
    .reduce((sum: number, tx: any) => sum + Math.abs(tx.amount), 0);

  const openDisputes = jobEvents.filter((e: any) => e.type === "DISPUTE_RESOLVED").length;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold font-display">Financial</h1>
        <p className="text-muted-foreground text-sm mt-1">Earnings and spending across your agent fleet</p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <TrendingUp className="size-4" />
              Total Earned
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold font-mono">{totalEarned.toFixed(2)}</p>
            <p className="text-xs text-muted-foreground mt-0.5">USDC from completed jobs</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <Coins className="size-4" />
              Bid Credits Used
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold font-mono">{bidCreditsUsed}</p>
            <p className="text-xs text-muted-foreground mt-0.5">across all agents</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <ShieldAlert className="size-4" />
              Resolved Disputes
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold font-mono">{openDisputes}</p>
            <p className="text-xs text-muted-foreground mt-0.5">in transaction history</p>
          </CardContent>
        </Card>
      </div>

      {/* Filter chips */}
      <div className="flex flex-wrap gap-2">
        {EVENT_TYPE_FILTERS.map((f) => (
          <Button
            key={f}
            variant={filter === f ? "default" : "outline"}
            size="sm"
            className="h-7 text-xs px-3"
            onClick={() => setFilter(f)}
          >
            {f === "ALL" ? "All" : (EVENT_TYPE_LABELS[f] ?? BID_TX_TYPE_LABELS[f] ?? f)}
          </Button>
        ))}
      </div>

      {/* Transaction table */}
      {!filteredRows.length ? (
        <Card>
          <CardContent className="py-16 text-center text-muted-foreground">
            <TrendingUp className="mx-auto h-10 w-10 mb-3 opacity-40" />
            <p className="font-medium">No transactions yet</p>
            <p className="text-sm mt-1">Financial activity will appear here once your agents complete jobs.</p>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border/50">
                  <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground">Date</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground">Agent</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground">Type</th>
                  <th className="text-right px-4 py-3 text-xs font-medium text-muted-foreground">Amount</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground">Status</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground">Tx</th>
                </tr>
              </thead>
              <tbody>
                {filteredRows.map((row) => (
                  <tr key={row.id} className="border-b border-border/30 hover:bg-muted/30 transition-colors">
                    <td className="px-4 py-3 text-xs text-muted-foreground whitespace-nowrap">
                      {new Date(row.date).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}
                    </td>
                    <td className="px-4 py-3 text-xs font-mono truncate max-w-[120px]">{row.agent}</td>
                    <td className="px-4 py-3">
                      <Badge variant="secondary" className="text-[10px] font-normal">{row.typeLabel}</Badge>
                    </td>
                    <td className={cn(
                      "px-4 py-3 text-right font-mono text-xs font-semibold",
                      row.amountSign === "+" ? "text-green-500" : row.amountSign === "-" ? "text-red-500" : ""
                    )}>
                      {row.amountSign}{row.amount}
                    </td>
                    <td className="px-4 py-3">
                      <Badge variant="outline" className="text-[10px]">{row.status}</Badge>
                    </td>
                    <td className="px-4 py-3">
                      {row.txHash ? (
                        <a
                          href={`https://basescan.org/tx/${row.txHash}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-1 text-xs text-primary hover:underline font-mono"
                        >
                          {row.txHash.slice(0, 8)}…
                          <ExternalLink className="size-3" />
                        </a>
                      ) : (
                        <span className="text-xs text-muted-foreground">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}
